import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LOST_REASONS } from "../theme.js";

type Props = {
  open: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
};

export function LostReasonModal({ open, onCancel, onConfirm }: Props): JSX.Element {
  const [selected, setSelected] = useState<string>(LOST_REASONS[0]);
  const [other, setOther] = useState<string>("");

  useEffect(() => {
    if (open) {
      setSelected(LOST_REASONS[0]);
      setOther("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onCancel]);

  const handleConfirm = (): void => {
    const reason =
      selected === "Other" ? (other.trim() || "Other") : selected;
    onConfirm(reason);
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onCancel}
        >
          <div className="absolute inset-0 bg-ink-950/80" />
          <motion.div
            className="relative w-[22rem] rounded-xl border border-ink-500 bg-ink-800 p-5 shadow-drag"
            initial={{ y: 8, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 8, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 380, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4">
              <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-slack-pink">
                Moving to Lost
              </div>
              <div className="mt-1 text-xl text-ink-50">
                Why did this deal fall through?
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              {LOST_REASONS.map((r) => (
                <label
                  key={r}
                  className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-[13px] transition-colors ${
                    selected === r
                      ? "border-slack-aubergineLight bg-slack-aubergine/25 text-ink-50"
                      : "border-ink-500 bg-ink-700/40 text-ink-50 hover:bg-ink-700"
                  }`}
                >
                  <input
                    type="radio"
                    name="lost-reason"
                    className="sr-only"
                    checked={selected === r}
                    onChange={() => setSelected(r)}
                  />
                  <span
                    className={`h-3 w-3 rounded-full border-2 ${
                      selected === r
                        ? "border-slack-aubergineLight bg-slack-pink"
                        : "border-ink-400"
                    }`}
                  />
                  <span>{r}</span>
                </label>
              ))}
              {selected === "Other" ? (
                <input
                  autoFocus
                  value={other}
                  onChange={(e) => setOther(e.target.value)}
                  maxLength={120}
                  placeholder="Short note (optional)"
                  className="mt-1 rounded-md border border-ink-500 bg-ink-900 px-3 py-2 text-[13px] text-ink-50 outline-none placeholder:text-ink-200 focus:border-slack-aubergineLight"
                />
              ) : null}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={onCancel}
                className="rounded-md border border-ink-500 px-3 py-1.5 text-[13px] text-ink-50 hover:border-ink-400"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="rounded-md border border-slack-pink bg-slack-pink/20 px-3 py-1.5 text-[13px] font-medium text-ink-50 hover:bg-slack-pink/30"
              >
                Mark as Lost
              </button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
